import { useState } from "react";
import { Send } from "lucide-react";
import { submitEnquiry, errMsg } from "@/lib/api";
import { isEmail } from "@/components/forms/FormKit";
import { CATEGORIES } from "@/data/services";

const TOPICS = [...CATEGORIES.map((c) => `${c.code}. ${c.label}`), "Outsourcing / white-label", "Free 8-hour trial", "Pricing question", "Other"];
const cls = "w-full border border-white/15 bg-white/[.04] px-3 py-2.5 text-[13px] text-white placeholder:text-[#7f94aa] outline-none focus:border-[#5AA9E6]";

/* Compact customer-support email form inside the floating widget. Submits a 'contact' enquiry (saved + emailed instantly). */
export const SupportChat = () => {
  const [f, setF] = useState({ name: "", email: "", topic: TOPICS[0], message: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return setErr("Please enter your name.");
    if (!isEmail(f.email)) return setErr("Please enter a valid email.");
    if (f.message.trim().length < 10) return setErr("Tell us a little more (at least 10 characters).");
    setErr(""); setBusy(true);
    try {
      const res = await submitEnquiry("contact", { name: f.name, email: f.email, subject: `Support chat — ${f.topic}`, message: f.message, source: "support-widget", consent: true });
      setDone(res);
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };

  if (done) return (
    <div className="p-5" data-testid="support-chat-success">
      <p className="font-display text-base font-bold text-white">Message sent to the drafting team.</p>
      <p className="mt-2 text-[13px] leading-relaxed text-[#c3d0dd]">Reference <span className="font-mono text-[#D8B66A]">{done.reference}</span>. We reply by email during business hours (Mon–Fri 8–6, Sat 9–2 EST).</p>
      <button type="button" onClick={() => { setDone(null); setF({ name: "", email: "", topic: TOPICS[0], message: "" }); }} className="u-link mt-4 font-mono text-[10px] uppercase tracking-[0.16em] text-white" data-testid="support-chat-again">Send another</button>
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-2.5 p-5" data-testid="support-chat-form" noValidate>
      <p className="text-[13px] text-[#c3d0dd]">Replies land in your inbox — usually within one business day.</p>
      <input className={cls} placeholder="Your name" value={f.name} onChange={set("name")} autoComplete="name" data-testid="support-name" aria-label="Your name" />
      <input className={cls} type="email" placeholder="Your email — we reply here" value={f.email} onChange={set("email")} autoComplete="email" data-testid="support-email" aria-label="Your email" />
      <select className={`${cls} [&>option]:text-[#102A43]`} value={f.topic} onChange={set("topic")} data-testid="support-topic" aria-label="Topic">
        {TOPICS.map((t) => <option key={t}>{t}</option>)}
      </select>
      <textarea className={`${cls} min-h-[84px] resize-none`} placeholder="Tell us about your survey drafting project…" value={f.message} onChange={set("message")} data-testid="support-message" aria-label="Message" />
      {err && <p className="text-[12px] text-[#ffb4a8]" role="alert" data-testid="support-error">{err}</p>}
      <button type="submit" disabled={busy} data-testid="support-submit" className="group inline-flex w-full items-center justify-center gap-2 bg-[#1769AA] px-4 py-3 font-display text-[12px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-[#2a7fc4] disabled:opacity-60">
        <Send className="h-4 w-4" />{busy ? "Sending…" : "Send to the drafting team"}
      </button>
      <p className="text-center font-mono text-[9px] uppercase tracking-[0.12em] text-[#7f94aa]">Sent to survey@geodrafting.com · by sending you agree to be contacted by email</p>
    </form>
  );
};
